"use client";

import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { getAccessToken } from "@/lib/auth";
import {
  applyTextDiff,
  base64ToUint8Array,
  uint8ArrayToBase64,
} from "./crdt-utils";

interface CollabUser {
  userId: string;
  userName: string;
  cursor?: { line: number; ch: number };
}

interface UseCollabArgs {
  documentId: string | undefined;
  fileId: string;
  initialContent: string;
  enabled: boolean;
  onRemoteContent: (newContent: string) => void;
}

/**
 * Hook que conecta el editor al WebSocket de colaboración de Yjs.
 * Maneja: conexión WS, sincronización Y.Doc, awareness de presencia,
 * y aplicación de diffs al pulsar del usuario.
 *
 * Devuelve la lista de usuarios activos (awareness) y un setter para
 * exponer el ytext/yDoc a la lógica de inserción de markdown.
 */
export function useCollab({
  documentId,
  fileId,
  initialContent,
  enabled,
  onRemoteContent,
}: UseCollabArgs) {
  const [activeUsers, setActiveUsers] = useState<CollabUser[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const ytextRef = useRef<Y.Text | null>(null);

  useEffect(() => {
    const token = getAccessToken();
    if (!documentId || !token || !enabled) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setActiveUsers([]);
      return;
    }

    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;
    const ytext = ydoc.getText("content");
    ytextRef.current = ytext;

    if (ytext.length === 0) {
      ytext.insert(0, initialContent || "");
    }

    const WS_BASE_URL = process.env.NEXT_PUBLIC_WS_URL || "ws://localhost:8000";
    const wsUrl = `${WS_BASE_URL.replace("http", "ws")}/ws/collab/${documentId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "connected") {
          ws.send(JSON.stringify({
            type: "awareness.update",
            payload: { cursor: { line: 0, ch: 0 } },
          }));
        } else if (message.type === "awareness.snapshot") {
          if (message.payload && message.payload.users) {
            setActiveUsers(message.payload.users);
          }
        } else if (message.type === "crdt.update") {
          const encodedUpdate = message.payload.update;
          if (encodedUpdate) {
            const updateBin = base64ToUint8Array(encodedUpdate);
            Y.applyUpdate(ydoc, updateBin, "remote");
            const newContent = ytext.toString();
            onRemoteContent(newContent);
          }
        }
      } catch (err) {
        console.error("Collab WS message error", err);
      }
    };

    ydoc.on("update", (update: Uint8Array, origin: unknown) => {
      if (origin !== "remote" && ws.readyState === WebSocket.OPEN) {
        const encoded = uint8ArrayToBase64(update);
        ws.send(JSON.stringify({
          type: "crdt.update",
          payload: {
            update: encoded,
            clientId: ydoc.clientID,
          },
        }));
      }
    });

    return () => {
      if (ws) {
        ws.close();
      }
      wsRef.current = null;
    };
  }, [documentId, fileId, enabled]);

  const applyLocalDiff = (oldText: string, newText: string) => {
    if (!ydocRef.current || !ytextRef.current) return;
    const ytext = ytextRef.current;
    ydocRef.current.transact(() => {
      applyTextDiff(ytext, oldText, newText);
    }, "local");
  };

  return { activeUsers, applyLocalDiff };
}
